import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { SelectField, TextField } from "@/components/ui/Field";
import { PageHeader } from "@/components/ui/PageHeader";
import { adminApi } from "@/lib/admin-api";
import { formatDateTime } from "@/lib/format";

const PAGE_SIZE = 50;
const GROUPS = ["auth", "ticket", "admin", "cli"] as const;

export function AuditPage() {
  const { t } = useTranslation();
  const [group, setGroup] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [offset, setOffset] = useState(0);

  const params = new URLSearchParams({ limit: String(PAGE_SIZE), offset: String(offset) });
  if (group) params.set("action", `${group}.`);
  if (from) params.set("date_from", `${from}T00:00:00Z`);
  if (to) params.set("date_to", `${to}T23:59:59Z`);
  const audit = useQuery({
    queryKey: ["admin", "audit", params.toString()],
    queryFn: () => adminApi.audit(params),
    placeholderData: keepPreviousData,
  });
  const total = audit.data?.total ?? 0;
  const resetPage =
    <T,>(setter: (v: T) => void) =>
    (value: T) => {
      setter(value);
      setOffset(0);
    };

  return (
    <>
      <PageHeader title={t("admin.audit.title")} />
      <Card className="overflow-hidden">
        <div className="grid gap-4 border-b border-line p-5 sm:grid-cols-[repeat(3,minmax(0,14rem))]">
          <SelectField
            label={t("admin.audit.filterAction")}
            value={group}
            onChange={(e) => resetPage(setGroup)(e.target.value)}
          >
            <option value="">{t("admin.audit.allActions")}</option>
            {GROUPS.map((g) => (
              <option key={g} value={g}>
                {t(`admin.audit.groups.${g}`)}
              </option>
            ))}
          </SelectField>
          <TextField
            label={t("admin.audit.filterFrom")}
            type="date"
            value={from}
            onChange={(e) => resetPage(setFrom)(e.target.value)}
          />
          <TextField
            label={t("admin.audit.filterTo")}
            type="date"
            value={to}
            onChange={(e) => resetPage(setTo)(e.target.value)}
          />
        </div>
        <div className="relative overflow-x-auto">
          <table className="stack-table w-full min-w-[52rem] text-left text-sm">
            <thead className="border-b border-line bg-surface-2 text-xs text-ink-muted">
              <tr>
                <th scope="col" className="px-5 py-3 font-medium">
                  {t("admin.audit.when")}
                </th>
                <th scope="col" className="px-5 py-3 font-medium">
                  {t("admin.audit.user")}
                </th>
                <th scope="col" className="px-5 py-3 font-medium">
                  {t("admin.audit.action")}
                </th>
                <th scope="col" className="px-5 py-3 font-medium">
                  {t("admin.audit.target")}
                </th>
                <th scope="col" className="px-5 py-3 font-medium">
                  {t("admin.audit.ip")}
                </th>
                <th scope="col" className="px-5 py-3 font-medium">
                  {t("admin.audit.details")}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-line">
              {audit.data?.items.map((entry) => (
                <tr key={entry.id} className="align-top hover:bg-surface-2">
                  <td className="px-5 py-2.5 whitespace-nowrap text-ink-muted tabular-nums">
                    {formatDateTime(entry.at)}
                  </td>
                  <td className="px-5 py-2.5" data-label={t("admin.audit.user")}>
                    {entry.username ?? "—"}
                  </td>
                  <td className="px-5 py-2.5 font-medium break-all" data-label={t("admin.audit.action")}>
                    {entry.action}
                  </td>
                  <td className="px-5 py-2.5 text-ink-muted" data-label={t("admin.audit.target")}>
                    {entry.target_type ? `${entry.target_type} ${entry.target_id ?? ""}` : "—"}
                  </td>
                  <td className="px-5 py-2.5 text-ink-muted tabular-nums" data-label={t("admin.audit.ip")}>
                    {entry.ip ?? "—"}
                  </td>
                  <td
                    className="max-w-xs px-5 py-2.5 text-xs break-words text-ink-subtle"
                    data-label={t("admin.audit.details")}
                  >
                    {entry.details ? JSON.stringify(entry.details) : ""}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {audit.data?.items.length === 0 && (
            <p className="px-5 py-6 text-sm text-ink-muted">{t("admin.audit.empty")}</p>
          )}
        </div>
        <footer className="flex flex-wrap items-center justify-between gap-3 border-t border-line px-5 py-3 text-sm">
          <span className="text-ink-muted tabular-nums">{t("admin.audit.total", { count: total })}</span>
          <div className="flex gap-2">
            <Button
              variant="secondary"
              disabled={offset === 0}
              onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
            >
              {t("common.previous")}
            </Button>
            <Button
              variant="secondary"
              disabled={offset + PAGE_SIZE >= total}
              onClick={() => setOffset(offset + PAGE_SIZE)}
            >
              {t("common.next")}
            </Button>
          </div>
        </footer>
      </Card>
    </>
  );
}
